import {queries} from '@/shared/integrations/zero'
import {RestVial} from '@cypro/engine/schema'
import {type GetPageQuery, type GetSingleQuery} from '@cypro/ui/utils/zero-virtualizer'
import {useCallback, useMemo, useState} from 'react'

export type RestVialStartRow = Pick<RestVial, 'id' | 'createdAt'>

export function useGetInfiniteRestVials({
  sort = {dir: 'desc', field: 'createdAt'},
  filters,
  limit = 25,
}: Omit<Partial<Parameters<typeof queries.stocks.listRestVials>[0]>, 'cursor'> = {}) {
  const [search, setSearch] = useState('')

  const listContextParams = useMemo(
    () => ({sort, filters, limit, search}),
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [
      sort?.dir,
      sort?.field,
      filters?.search,
      filters?.categories,
      filters?.stockLocations,
      filters?.therapySequences,
      filters?.status,
      limit,
      search,
    ]
  )

  const getPageQuery: GetPageQuery<RestVial, RestVialStartRow> = useCallback(
    ({limit: pageLimit, start, dir}) => {
      const cursor =
        start?.id && start?.createdAt
          ? {
              id: start.id,
              direction: dir,
              createdAt: start.createdAt,
            }
          : undefined

      return {
        query: queries.stocks.listRestVials({
          cursor,
          limit: pageLimit,
          sort,
          filters: {
            ...filters,
            ...(search ? {search} : {}),
          },
        }),
      }
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [listContextParams]
  )

  // @ts-expect-error schema type mismatch (works at runtime)
  const getSingleQuery: GetSingleQuery<RestVial> = useCallback(
    ({id}) => ({
      query: queries.stocks.getRestVialById(id),
    }),
    []
  )

  const toStartRow = useCallback(
    (row: RestVial): RestVialStartRow => ({
      id: row.id,
      createdAt: row.createdAt,
    }),
    []
  )

  return {
    search,
    onSearchChange: setSearch,
    getPageQuery,
    getSingleQuery,
    toStartRow,
    listContextParams,
  }
}
